import { Outlet, useNavigate, useOutletContext } from 'react-router';
import {
    RequestMethod,
    RequestStatus,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import {
    Add,
    CheckCircleOutlined,
    HighlightOff,
    PictureAsPdf,
    QrCode2,
    RemoveCircleOutlineOutlined,
} from '@mui/icons-material';
import {
    Button,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { SeparationBar } from '../layout/components/SeparationBar.tsx';
import { GuestDayPassConfig } from 'product-shared/product-template/ProductTemplate.ts';
import { EmptyResults } from '../shared-mui-components/ListMessages.tsx';
import { type JSX, useState } from 'react';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import DeleteIcon from '@mui/icons-material/Delete';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';

interface GuestDayPassInfo {
    guestDayPassConfigInfos: GuestDayPassConfigInfo[];
    stacks: GuestDayPassStack[];
}

interface GuestDayPassConfigInfo {
    contractTemplateId: number;
    guestDayPassConfig: GuestDayPassConfig;
}

interface GuestDayPassStack {
    stackId: string;
    stackState: OperatorGuestDayStackStatus;
    typeName: Message;
    stackName: string;
    totalCount: number;
    validCount: number;
    usedCount: number;
    createdAt: string;
    validTo: string;
    selectedDays: number;
}

export const enum OperatorGuestDayStackStatus {
    ACTIVE = 'ACTIVE',
    EXPIRED = 'EXPIRED',
    DELETED = 'DELETED',
}

export function OperatorGuestDayPassOverview() {
    const [gustDayPassInfoState, refetchGuestDayPassInfo] = useServerFetch<
        GuestDayPassInfo,
        object
    >(
        () => ({
            url: '/ui-api/operator-account/offstreet-guest-day-pass-stacks',
        }),
        {},
    );
    const navigate = useNavigate();

    return (
        <MainContent>
            <OperatorAsyncLoadedSection
                requestState={gustDayPassInfoState}
                pendingLoaderPreset={
                    LoadingSpinnerPresets.FillAllSpaceAndCenter
                }
                render={dayPassInfo => (
                    <>
                        <HeaderWithButtons
                            icon={QrCode2}
                            title={
                                <Localized
                                    de="Tagespässe"
                                    fr="Pass journaliers"
                                    it="Pass giornalieri"
                                    en="Daily passes"
                                />
                            }
                            headerButtons={
                                <Stack
                                    direction="row"
                                    spacing={3}
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <Tooltip
                                        title={
                                            dayPassInfo.guestDayPassConfigInfos
                                                .length < 1 ? (
                                                <Localized
                                                    de="Es gibt keine aktive Tagespass Konfiguration"
                                                    fr="Il n’y a aucune configuration active de pass journalier"
                                                    it="Non esiste alcuna configurazione attiva del pass giornaliero"
                                                    en="There is no active day pass configuration"
                                                />
                                            ) : undefined
                                        }
                                        placement="bottom"
                                    >
                                        <Button
                                            variant="contained"
                                            startIcon={<Add />}
                                            color="primary"
                                            disabled={
                                                dayPassInfo
                                                    .guestDayPassConfigInfos
                                                    .length < 1
                                            }
                                            onClick={() => navigate('add')}
                                        >
                                            <Localized
                                                de="Stapel generieren"
                                                fr="Générer set"
                                                it="Genera set"
                                                en="Generate stack"
                                            />
                                        </Button>
                                    </Tooltip>
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            navigate('../guest-products')
                                        }
                                    >
                                        <Localized
                                            de="Schliessen"
                                            fr="Fermer"
                                            it="Chiudi"
                                            en="Close"
                                        />
                                    </Button>
                                </Stack>
                            }
                        />
                        <SeparationBar />
                        <OperatorOffstreetGuestDayPassTable
                            stacks={dayPassInfo.stacks}
                            refetchGuestDayPassInfo={refetchGuestDayPassInfo}
                        />
                        <OffstreetGuestDayPassOutlet
                            context={{
                                refetchDayPassInfo: refetchGuestDayPassInfo,
                                guestDayPassConfigInfos:
                                    dayPassInfo.guestDayPassConfigInfos,
                            }}
                        />
                    </>
                )}
            />
        </MainContent>
    );
}

function OperatorOffstreetGuestDayPassTable({
    stacks,
    refetchGuestDayPassInfo,
}: {
    stacks: GuestDayPassStack[];
    refetchGuestDayPassInfo: () => void;
}) {
    if (stacks.length === 0) {
        return <EmptyResults hasFilter={false} />;
    }
    return (
        <TableContainer>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Localized
                                de="Status"
                                fr="État"
                                it="Stato"
                                en="State"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized de="Typ" fr="Type" it="Tipo" en="Type" />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Stapel"
                                fr="Set"
                                it="Set"
                                en="Stack"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Total"
                                fr="Total"
                                it="Totale"
                                en="Total"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Gültige"
                                fr="Valides"
                                it="Validi"
                                en="Valid"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Gebrauchte"
                                fr="Utilisés"
                                it="Usati"
                                en="Used"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Erstellungsdatum"
                                fr="Date de création"
                                it="Data di creazione"
                                en="Creation date"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Ablaufdatum"
                                fr="Date d'échéance"
                                it="Data scadenza"
                                en="Expiration date"
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Localized
                                de="Aktionen"
                                fr="Actions"
                                it="Azioni"
                                en="Actions"
                            />
                        </TableCell>
                        <TableCell>PDF</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {stacks.map(stack => (
                        <GuestDayPassStackRow
                            key={stack.stackId}
                            stack={stack}
                            refetchGuestDayPassInfo={refetchGuestDayPassInfo}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function GuestDayPassStackRow({
    stack,
    refetchGuestDayPassInfo,
}: {
    stack: GuestDayPassStack;
    refetchGuestDayPassInfo: () => void;
}) {
    const { operatorExternalId } = useOperatorContext();
    const { language } = useLanguage();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    return (
        <TableRow>
            <TableCell>
                <OperatorOffstreetGuestDayPassStatusIcon
                    status={stack.stackState}
                />
            </TableCell>
            <TableCell>
                <Localized {...stack.typeName} />
            </TableCell>
            <TableCell>
                <Localized
                    de={`${stack.stackName} (${
                        stack.selectedDays === 1
                            ? '1 Tag'
                            : `${stack.selectedDays} Tage`
                    })`}
                    fr={`${stack.stackName} (${
                        stack.selectedDays === 1
                            ? '1 jour'
                            : `${stack.selectedDays} jours`
                    })`}
                    it={`${stack.stackName} (${
                        stack.selectedDays === 1
                            ? '1 giorno'
                            : `${stack.selectedDays} giorni`
                    })`}
                    en={`${stack.stackName} (${
                        stack.selectedDays === 1
                            ? '1 day'
                            : `${stack.selectedDays} days`
                    })`}
                />
            </TableCell>
            <TableCell>{stack.totalCount}</TableCell>
            <TableCell>{stack.validCount}</TableCell>
            <TableCell>
                {stack.usedCount === 0 ? EmDash : stack.usedCount}
            </TableCell>
            <TableCell>
                {Formatter.dayMonthYear(DateTime.fromISO(stack.createdAt))}
            </TableCell>
            <TableCell>
                {Formatter.dayMonthYear(DateTime.fromISO(stack.validTo))}
            </TableCell>
            <TableCell>
                <Stack
                    direction="row"
                    sx={{
                        display: 'flex',
                        flexShrink: 1,
                    }}
                >
                    {stack.stackState ===
                        OperatorGuestDayStackStatus.ACTIVE && (
                        <Tooltip
                            title={
                                <Localized
                                    de="Löschen"
                                    fr="Supprimer"
                                    it="Elimina"
                                    en="Delete"
                                />
                            }
                            placement="right"
                        >
                            <IconButton
                                color="primary"
                                onClick={() => setDeleteModalOpen(true)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </TableCell>
            <TableCell>
                {stack.stackState === OperatorGuestDayStackStatus.ACTIVE ? (
                    <a
                        target="_blank"
                        href={urlWithOperatorExternalId(
                            `/ui-api/operator-account/offstreet-guest-day-pass/stack/${stack.stackId}/pdf?language=${language}`,
                            operatorExternalId,
                        )}
                        rel="noreferrer"
                    >
                        <PictureAsPdf
                            sx={{
                                color: theme => theme.palette.primary.main,
                                '&:hover': {
                                    color: theme => theme.palette.blue.electric,
                                },
                            }}
                        />
                    </a>
                ) : (
                    EmDash
                )}
            </TableCell>
            <DeleteStackModal
                open={deleteModalOpen}
                closeModal={() => setDeleteModalOpen(false)}
                refetchGuestDayPassInfo={refetchGuestDayPassInfo}
                stack={stack}
            />
        </TableRow>
    );
}

function DeleteStackModal({
    open,
    closeModal,
    refetchGuestDayPassInfo,
    stack,
}: {
    open: boolean;
    closeModal: () => void;
    refetchGuestDayPassInfo: () => void;
    stack: GuestDayPassStack;
}) {
    const [deleteStackState, deleteStack] = useServerWrite<null, null, null>(
        () => ({
            url: `/ui-api/operator-account/offstreet-guest-day-pass/stack/${stack.stackId}/delete`,
            method: RequestMethod.PUT,
        }),
    );
    useServerSuccessEffect(deleteStackState, () => {
        refetchGuestDayPassInfo();
        closeModal();
    });

    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Stapel löschen"
                    fr="Supprimer le set"
                    it="Elimina set"
                    en="Detete stack"
                />
            }
            confirmLabel={
                <Localized
                    de="Löschen"
                    fr="Supprimer"
                    it="Elimina"
                    en="Delete"
                />
            }
            onConfirm={() => deleteStack()}
            confirmDisabled={deleteStackState.status === RequestStatus.PENDING}
            onCancel={() => closeModal()}
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <>
                <BackendRequestErrorMessage requestState={deleteStackState} />

                <p>
                    <Localized
                        de={`Möchten Sie die noch nicht verwendeten Tagespässe aus dem Stapel «${stack.stackName}» löschen?`}
                        fr={`Voulez-vous supprimer les pass journaliers non utilisés contenus dans le set «${stack.stackName}»?`}
                        it={`Vuole eliminare i pass giornalieri non ancora utilizzati contenuti nel set «${stack.stackName}»?`}
                        en={`Do you want to delete the unused day passes contained in the set «${stack.stackName}»?`}
                    />
                </p>
                <p>
                    <Localized
                        de="ACHTUNG: diese Aktion kann nicht rückgängig gemacht werden."
                        fr="ATTENTION: cette opération est irréversible."
                        it="ATTENZIONE: questa operazione non sarà reversibile."
                        en="WARNING: this operation cannot be reversed."
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

export function OperatorOffstreetGuestDayPassStatusIcon({
    status,
}: {
    status: OperatorGuestDayStackStatus;
}): JSX.Element {
    switch (status) {
        case OperatorGuestDayStackStatus.ACTIVE:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="gültig"
                            fr="valable"
                            it="valido"
                            en="valid"
                        />
                    }
                    placement="right"
                >
                    <CheckCircleOutlined color="success" />
                </Tooltip>
            );
        case OperatorGuestDayStackStatus.EXPIRED:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="abgelaufen"
                            fr="expiré"
                            it="scaduto"
                            en="expired"
                        />
                    }
                    placement="right"
                >
                    <RemoveCircleOutlineOutlined color="disabled" />
                </Tooltip>
            );
        case OperatorGuestDayStackStatus.DELETED:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="gelöscht"
                            fr="supprimé"
                            it="eliminato"
                            en="deleted"
                        />
                    }
                    placement="right"
                >
                    <HighlightOff color="error" />
                </Tooltip>
            );
    }
}

export function useOffstreetGuestDayPassOutlet() {
    return useOutletContext<OffstreetGuestDayPassContext>();
}

function OffstreetGuestDayPassOutlet({
    context,
}: {
    context: OffstreetGuestDayPassContext;
}) {
    return <Outlet context={context} />;
}

export interface OffstreetGuestDayPassContext {
    refetchDayPassInfo: () => void;
    guestDayPassConfigInfos: GuestDayPassConfigInfo[];
}
